import * as React from 'react';
import { ReactNode } from 'react';
import { useDropzone, DropzoneOptions } from 'react-dropzone';
import styled from 'styled-components';

import { TextButton } from '@creditornot/cb-button';
import { typographyCss } from '@creditornot/cb-ingredients';
import { color } from '@creditornot/cb-ingredients/design-tokens';

import { Stack } from './Stack';
import { useFormatMessage } from '../../hooks/useFormatMessage';

const Root = styled.div<{ $isActive: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px;
  border: 2px dashed ${({ $isActive }) => ($isActive ? color.borderSelected : color.border)};
  border-radius: 8px;
  outline: none;
`;

const StyledHintText = styled.div`
  ${typographyCss.footnote};
  color: ${color.textSubdued};
`;

const SelectedFile = styled.li`
  font-weight: 700;
`;

type Props = Pick<DropzoneOptions, 'accept' | 'onDrop'> & {
  id?: string;
  multiple?: boolean;
  value?: File;
  selectionText?: string;
  selectedFiles?: string[];
  buttonText?: string;
  hintText?: ReactNode;
};

export const FileInput: React.FC<Props> = ({
  id,
  multiple = false,
  onDrop,
  accept,
  selectedFiles = [],
  buttonText,
  hintText,
}) => {
  const formatMessage = useFormatMessage();
  const { getRootProps, getInputProps, isDragAccept } = useDropzone({
    accept,
    multiple,
    onDrop,
  });

  return (
    <Root {...getRootProps()} $isActive={isDragAccept}>
      <input data-test-id="file-input" id={id} {...getInputProps()} />
      <div>
        {selectedFiles.length > 0 ? (
          <Stack alignItems="center" gap="regularMedium" tag="ul">
            {selectedFiles.map((name) => (
              <SelectedFile key={name}>{name}</SelectedFile>
            ))}
          </Stack>
        ) : (
          <Stack alignItems="center" gap="regularMedium">
            <div>
              <TextButton>
                {buttonText || formatMessage('components.file_input.select_file')}
              </TextButton>
              &nbsp;
              <span>{formatMessage('components.file_input.drop')}</span>
            </div>
            {hintText && <StyledHintText>{hintText}</StyledHintText>}
          </Stack>
        )}
      </div>
    </Root>
  );
};
